import {Component, Inject, OnInit, signal} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {FormControl, ReactiveFormsModule, Validators} from '@angular/forms';
import {AsyncPipe, CommonModule} from '@angular/common';
import {MatButton} from '@angular/material/button';
import {MatDialogActions, MatDialogContent, MatDialogTitle} from '@angular/material/dialog';
import {FaultyVehicleDataService, VehicleDataFieldEnum} from '../../service/FaultyVehicleData.service';
import {EcuNamesService} from '../../service/Ecu-names.service';
import {MatFormField, MatLabel} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import {CorrectEcuBuildModel} from '../../uploading-actions/user-inputs/user-inputs.component';
import {map, startWith, Observable} from 'rxjs';
import {EcuProducersService} from "../../service/ecu-producers.service";


@Component({
    selector: 'app-vehicle-data-confirmation-dialog',
    standalone: true,
    imports: [
        MatButton,
        ReactiveFormsModule,
        MatDialogActions,
        MatDialogContent,
        MatDialogTitle,
        MatFormField,
        MatLabel,
        MatInput,
        MatProgressSpinner,
        AsyncPipe,
        CommonModule,
        MatAutocompleteModule
    ],
    templateUrl: './vehicle-data-confirmation-dialog.component.html',
    styleUrl: './vehicle-data-confirmation-dialog.component.css',
})
export class VehicleDataConfirmationDialogComponent implements OnInit {
    showEcuBuildInput = signal<boolean>(false);
    isLoading = signal<boolean>(false);

    newValueControl = new FormControl<string | CorrectEcuBuildModel>('', [Validators.required]);

    options: CorrectEcuBuildModel[] = [];
    filteredOptions: Observable<CorrectEcuBuildModel[]>;

    constructor(public dialogRef: MatDialogRef<VehicleDataConfirmationDialogComponent>, @Inject(MAT_DIALOG_DATA)
                public data: {
                    field: VehicleDataFieldEnum;
                    faultyValue: string;
                    pureOriginalIds: number[];
                }, private faultyVehicleDataService: FaultyVehicleDataService, private ecuNamesService: EcuNamesService, private ecuProducersService: EcuProducersService
    ) {
    }

    ngOnInit() {
        switch (this.data.field) {
            case VehicleDataFieldEnum.ECU_BUILD:
                this.ecuNamesService.getCorrectEcuNamesOptions().then(() => {
                    this.options = this.ecuNamesService.correctEcuBuildNames();
                    this.initializeAutocomplete();
                }).catch(err => {
                    console.error('Failed to load ECU Build Names', err);
                    this.options = [];
                    this.initializeAutocomplete();
                });
                break;

            case VehicleDataFieldEnum.VEHICLE_TYPE:
                this.faultyVehicleDataService.getVehicleTypes().subscribe({
                    next: res => {
                        this.options = res.map(name => ({correctEcuBuildNames: name}));
                        this.initializeAutocomplete();
                    },
                    error: err => {
                        console.error('Failed to load Vehicle Types', err);
                        this.options = [];
                        this.initializeAutocomplete();
                    }
                });
                break;

            case VehicleDataFieldEnum.VEHICLE_PRODUCER:
                this.faultyVehicleDataService.getVehicleProducers().subscribe({
                    next: res => {
                        this.options = res.map(name => ({correctEcuBuildNames: name}));
                        this.initializeAutocomplete();
                    },
                    error: err => {
                        console.error('Failed to load Vehicle Producers', err);
                        this.options = [];
                        this.initializeAutocomplete();
                    }
                });
                break;

            case VehicleDataFieldEnum.ECU_PRODUCER:
                this.ecuProducersService.loadCorrectEcuProducers().then(list => {
                    this.options = list.map(name => ({correctEcuBuildNames: name}));
                    this.initializeAutocomplete();
                }).catch(err => {
                    console.error('Failed to load ECU Producers', err);
                    this.options = [];
                    this.initializeAutocomplete();
                });
                break;

            default:
                this.options = [];
                this.initializeAutocomplete();
                break;
        }
    }


    getFieldLabel(): string {
        const label = this.data.field.replace(/_/g, ' ').toLowerCase();
        return label.charAt(0).toUpperCase() + label.slice(1);
    }

    initializeAutocomplete() {
        this.filteredOptions = this.newValueControl.valueChanges.pipe(
            startWith(''),
            map(value => {
                const name = typeof value === 'string' ? value : value?.correctEcuBuildNames;
                return name ? this._filter(name) : [...this.options];
            })
        );
    }

    private _filter(name: string): CorrectEcuBuildModel[] {
        const filterValue = name.toLowerCase();
        return this.options.filter(option =>
            option.correctEcuBuildNames.toLowerCase().includes(filterValue)
        );
    }

    displayFn(user: CorrectEcuBuildModel): string {
        return user && user.correctEcuBuildNames ? user.correctEcuBuildNames : '';
    }

    confirmedOption() {
        this.showEcuBuildInput.set(true);
    }

    canceledOption() {
        this.dialogRef.close(false);
    }

    async submitNewEcuBuild() {
        if (this.newValueControl.invalid) {
            this.faultyVehicleDataService.notificationService.showErrorMessage(
                'Please enter a valid value.',
                'Error'
            );
            return;
        }

        const newValue =
            typeof this.newValueControl.value === 'string'
                ? this.newValueControl.value
                : this.newValueControl.value?.correctEcuBuildNames;

        if (!newValue) {
            this.faultyVehicleDataService.notificationService.showErrorMessage(
                'New value cannot be empty.',
                'Error'
            );
            return;
        }

        this.isLoading.set(true);

        try {
            const response = await this.faultyVehicleDataService.updateFaultyPureOriginals(
                this.data.pureOriginalIds,
                newValue
            );

            this.faultyVehicleDataService.notificationService.showSuccessMessage(
                response.message || 'Successfully updated!',
                'Success'
            );
            this.dialogRef.close({success: true, updatedIds: response.pureOriginalIds});
        } catch (error: any) {
            this.faultyVehicleDataService.notificationService.showErrorMessage(
                error.message || 'Update failed.',
                'Error'
            );
        }

        this.isLoading.set(false);
    }
}
